import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import Meeting from "@/services/meeting";
import type { GetMeeting } from "@/dtos/Meeting.dto";

export interface MeetingStatsParams {
  organizationId: string;
  createdById?: string;
  startDate?: string;
  endDate?: string;
  offset?: number;
  contactOwnerId?: string | null;
}

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

export function useMeetingStats(
  params: Ref<MeetingStatsParams | null> | MeetingStatsParams | null
) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["meetingStats", "disabled"];
    return [
      "meetingStats",
      p.organizationId,
      p.createdById || "",
      p.startDate || "",
      p.endDate || "",
      p.contactOwnerId ?? "all",
    ];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return p !== null && p !== undefined && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const queryParams: GetMeeting = {
        organizationId: p.organizationId,
      };

      if (p.createdById) queryParams.id = p.createdById;
      if (p.startDate) queryParams.startDate = p.startDate;
      if (p.endDate) queryParams.endDate = p.endDate;
      if (p.offset !== undefined) queryParams.offset = p.offset;
      if (p.contactOwnerId !== undefined) queryParams.contactOwnerId = p.contactOwnerId;

      const result = await Meeting.getMeetingStats(queryParams);

      if (result.data.status) {
        return result.data.data;
      }

      throw new Error(result.data.msg || "Failed to load meeting stats");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
